"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useV2Translation } from "@/lib/i18n/use-v2-translation";
import type { Language } from "@/lib/i18n/translations";

function LanguageToggle() {
  const { lang } = useV2Translation();

  const switchLang = (newLang: Language) => {
    if (typeof window === "undefined") return;
    const pathname = window.location.pathname;
    const hasLocale = /^\/(fr|en)(\/|$)/.test(pathname);
    let newPath: string;
    if (hasLocale) {
      newPath = pathname.replace(/^\/(fr|en)/, `/${newLang}`);
    } else {
      newPath = `/${newLang}${pathname}`;
    }
    if (newPath !== pathname) {
      window.location.href = newPath;
    }
  };

  return (
    <div className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest">
      <button
        onClick={() => switchLang("fr")}
        className={`px-1.5 py-1 transition-colors ${
          lang === "fr" ? "text-gold" : "text-ink/40 hover:text-ink"
        }`}
        aria-label="Switch to French"
      >
        FR
      </button>
      <span className="text-ink/20">/</span>
      <button
        onClick={() => switchLang("en")}
        className={`px-1.5 py-1 transition-colors ${
          lang === "en" ? "text-gold" : "text-ink/40 hover:text-ink"
        }`}
        aria-label="Switch to English"
      >
        EN
      </button>
    </div>
  );
}

export const Navbar = () => {
  const { t } = useV2Translation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.home, href: "#top" },
    { label: t.nav.work, href: "#proof" },
    { label: t.nav.stories, href: "#stories" },
    { label: t.nav.about, href: "#disruptor" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-paper/85 backdrop-blur-md border-b border-line"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center group"
        >
          <Image
            src="/images/léman-it.png"
            alt="Léman IT"
            width={140}
            height={65}
            className="h-10 lg:h-[50px] w-auto object-contain"
            priority
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
              className="font-mono text-xs uppercase tracking-widest text-ink hover:text-gold transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden lg:block">
            <LanguageToggle />
          </div>
          <a
            href="#booking"
            data-testid="nav-cta-book"
            className="btn-primary hidden md:inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-widest"
          >
            {t.nav.cta}
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-line bg-paper"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-mono text-xs uppercase tracking-widest text-ink"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#booking"
              onClick={() => setOpen(false)}
              className="btn-primary inline-flex items-center justify-between px-5 py-3 font-mono text-[11px] uppercase tracking-widest"
            >
              {t.nav.cta}
              <ArrowUpRight size={14} />
            </a>
            <div className="pt-2 border-t border-line">
              <LanguageToggle />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
